import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, DownloadSimple } from "@phosphor-icons/react";

const DISMISS_KEY = "tain_install_dismissed_at";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const ua = window.navigator.userAgent || "";
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsIOS(ios);
    setInstalled(standalone);

    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    const recentlyDismissed = dismissedAt && Date.now() - dismissedAt < DISMISS_MS;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!recentlyDismissed) setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // Show iOS custom prompt after short delay (no beforeinstallprompt on iOS)
    if (ios && !standalone && !recentlyDismissed) {
      const t = setTimeout(() => setVisible(true), 5000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (installed || !visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed left-3 right-3 bottom-24 md:bottom-6 md:left-auto md:right-6 md:max-w-sm z-50 glass rounded-2xl p-4 shadow-2xl shadow-black/60 border border-white/10"
    >
      <button
        data-testid="install-dismiss"
        onClick={dismiss}
        className="absolute top-2 right-2 p-1 rounded-md hover:bg-white/10 text-white/60"
      >
        <X size={16} weight="bold" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center shrink-0">
          <DownloadSimple size={22} weight="bold" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-white">Install Tain app</p>
          {isIOS ? (
            <p className="text-xs text-white/70 mt-1 leading-relaxed">
              Tap <span className="font-bold">Share</span> in Safari, then{" "}
              <span className="font-bold">Add to Home Screen</span>.
            </p>
          ) : (
            <p className="text-xs text-white/70 mt-1 leading-relaxed">
              Get a native-like experience with faster loads, offline support and push notifications.
            </p>
          )}
        </div>
      </div>
      {!isIOS && deferred && (
        <Button
          data-testid="install-accept"
          onClick={install}
          className="w-full mt-3 bg-white text-black hover:bg-white/90 font-heading font-bold"
        >
          Install
        </Button>
      )}
    </div>
  );
}
